/**
 * Home View Module
 * Renders the home screen with daily practice card and series continuity
 */

import appState from '../../core/app-state.js';
import { i18nService } from '../../services/i18n-service.js';
import { generateDailySet, findSeriesContinuityClip } from '../daily-set/daily-set-generator.js';

/**
 * Callback functions type
 */
interface HomeCallbacks {
  isClipCompleted: (clipId: string) => boolean;
  setCurrentClip: (index: number) => void;
  switchMode: (mode: 'home' | 'library' | 'exercise') => void;
}

// Import functions that will be called from home view
// These will be registered via registerCallbacks
let isClipCompleted: ((clipId: string) => boolean) | undefined;
let setCurrentClip: ((index: number) => void) | undefined;
let switchMode: ((mode: 'home' | 'library' | 'exercise') => void) | undefined;

/**
 * Register callbacks for functions defined in main.js
 * This allows the home module to call main.js functions without circular dependencies
 */
export function registerHomeCallbacks(callbacks: HomeCallbacks): void {
  isClipCompleted = callbacks.isClipCompleted;
  setCurrentClip = callbacks.setCurrentClip;
  switchMode = callbacks.switchMode;
}

/**
 * Get the count of unique days with activity this week
 */
function getWeeklyActivityCount(): number {
  return appState.weeklyActivityDays.size;
}

/**
 * Render the home view with daily practice card and series continuity
 */
export function renderHomeView(): void {
  const homeContainer = document.getElementById('home-container');
  if (!homeContainer) return;
  
  // Clear existing content
  homeContainer.innerHTML = '';
  
  // Generate daily set if not already done
  if (!appState.dailySet || appState.dailySet.length === 0) {
    generateDailySet('auto');
  }
  
  // Calculate progress
  const totalCount = appState.dailySet.length;
  const completedCount = appState.dailySet.filter(id => isClipCompleted && isClipCompleted(id)).length;
  const hasInProgress = completedCount > 0 && completedCount < totalCount;
  const isCompleted = completedCount === totalCount && totalCount > 0;
  
  // Home heading
  const heading = document.createElement('h1');
  heading.id = 'home-heading';
  heading.className = 'home-heading';
  heading.textContent = i18nService.getString('home') || 'Home';
  heading.tabIndex = -1;
  homeContainer.appendChild(heading);
  
  // Main card container
  const mainCard = document.createElement('div');
  mainCard.className = 'home-main-card';
  
  // Primary CTA Section
  const ctaSection = document.createElement('div');
  ctaSection.className = 'home-cta-section';
  
  // Start/Resume button
  const ctaButton = document.createElement('button');
  ctaButton.className = 'home-cta-button';
  
  if (isCompleted) {
    // Static "Done for Today" message (not a button)
    const doneMessage = document.createElement('div');
    doneMessage.className = 'home-done-message';
    doneMessage.textContent = i18nService.getString('doneForToday') || "Congratulations! You've reached your daily target.";
    ctaSection.appendChild(doneMessage);
  } else {
    ctaButton.textContent = hasInProgress ? (i18nService.getString('resumeDailySet') || 'Resume Daily Practice') : (i18nService.getString('startDailySet') || 'Start Daily Practice');
    ctaButton.addEventListener('click', () => {
      // Start with first incomplete clip in daily set
      const nextClipId = appState.dailySet.find(id => isClipCompleted && !isClipCompleted(id));
      if (nextClipId) {
        const clipIndex = appState.clips.findIndex(c => c.id === nextClipId);
        if (clipIndex !== -1) {
          if (setCurrentClip) setCurrentClip(clipIndex);
          if (switchMode) switchMode('exercise');
        }
      }
    });
    ctaSection.appendChild(ctaButton);
  }
  
  // Progress subtext
  const progressText = document.createElement('div');
  progressText.className = 'home-progress-subtext';
  
  const userLevel = (appState.preferences['cefrLevel'] as string | undefined) || 'B2';
  
  // Always show just progress and level (no minute estimate on home view)
  progressText.textContent = `${completedCount}/${totalCount} • ${i18nService.getString('level') || 'Level:'} ${userLevel}`;
  
  ctaSection.appendChild(progressText);
  mainCard.appendChild(ctaSection);
  
  // Weekly activity display (below progress)
  const activityCount = getWeeklyActivityCount();
  const weeklyActivityDiv = document.createElement('div');
  weeklyActivityDiv.className = 'home-activity-summary';
  weeklyActivityDiv.textContent = `${i18nService.getString('activeThisWeek') || 'Active this week:'} ${activityCount} ${activityCount === 1 ? (i18nService.getString('day') || 'day') : (i18nService.getString('days') || 'days')}`;
  mainCard.appendChild(weeklyActivityDiv);
  
  // When completed: show "View Library" link instead of "Do More" button
  if (isCompleted) {
    const viewLibraryLink = document.createElement('button');
    viewLibraryLink.className = 'btn--link w-full';
    viewLibraryLink.textContent = i18nService.getString('viewLibrary') || 'View Library';
    viewLibraryLink.addEventListener('click', () => {
      if (switchMode) switchMode('library');
    });
    mainCard.appendChild(viewLibraryLink);
  }
  
  homeContainer.appendChild(mainCard);
  
  // Continue Series card (if applicable)
  const continuityClip = findSeriesContinuityClip(appState.clips.filter(c => isClipCompleted && !isClipCompleted(c.id)));
  if (continuityClip && !appState.dailySet.includes(continuityClip.id)) {
    const seriesCard = document.createElement('div');
    seriesCard.className = 'home-series-card';
    
    const seriesTitle = document.createElement('h3');
    seriesTitle.className = 'home-series-title';
    seriesTitle.textContent = i18nService.getString('continueSeries') || 'Continue Series';
    seriesCard.appendChild(seriesTitle);
    
    const seriesClipTitle = document.createElement('div');
    seriesClipTitle.className = 'card__series-label';
    seriesClipTitle.textContent = continuityClip.title;
    seriesCard.appendChild(seriesClipTitle);
    
    const continueButton = document.createElement('button');
    continueButton.className = 'home-series-button';
    continueButton.textContent = i18nService.getString('start') || 'Start';
    continueButton.addEventListener('click', () => {
      const clipIndex = appState.clips.findIndex(c => c.id === continuityClip.id);
      if (clipIndex !== -1) {
        if (setCurrentClip) setCurrentClip(clipIndex);
        if (switchMode) switchMode('exercise');
      }
    });
    seriesCard.appendChild(continueButton);
    
    homeContainer.appendChild(seriesCard);
  }
  
  // View Library link (only shown when not completed - completed shows it in the main card)
  if (!isCompleted) {
    const libraryLink = document.createElement('button');
    libraryLink.className = 'btn--link w-full';
    libraryLink.textContent = i18nService.getString('viewLibrary') || 'View Library';
    libraryLink.addEventListener('click', () => {
      if (switchMode) switchMode('library');
    });
    homeContainer.appendChild(libraryLink);
  }
}
